import React from 'react';
import { Card, Select, Button, Spin, Popconfirm } from 'antd';
import { ArrowRightOutlined, ArrowLeftOutlined, ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ReplayRouteDetail } from '../graphql/getReplayRouteDetail';
import { ReplayManualVersion } from '../graphql/listReplayManualVersions';
import { ReplayMetricsFields } from '../graphql/types';
import { ReplayRouteOption } from '../hooks/useReplayRoute';
import { REPLAY_PRIMARY } from '../theme';
import { METRIC_SPECS, MetricSpec, compare, Diff, fmtDur } from '../utils/metricsFormat';

const toneClass: Record<Diff['tone'], string> = {
    better: 'text-green-600',
    worse: 'text-red-500',
    even: 'text-gray-400',
    na: 'text-gray-400',
};

// 策略对比表各指标列宽
const COLW: Record<string, string> = {
    totalDistanceKm: 'w-12',
    totalDurationMinutes: 'w-14',
    leftTurnCount: 'w-8',
    uTurnCount: 'w-8',
    trafficLightCount: 'w-11',
};

// 表内距离只显示数值（列名已表明单位）
const cellValue = (spec: MetricSpec, m: ReplayMetricsFields | null) =>
    spec.key === 'totalDistanceKm'
        ? m?.totalDistanceKm != null
            ? m.totalDistanceKm.toFixed(1)
            : '—'
        : spec.fmt(m?.[spec.key]);

/** 有符号差值文案：-2.3 km / +3 / 持平 */
function DeltaText({ spec, diff }: { spec: MetricSpec; diff: Diff }) {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;
    if (diff.tone === 'na') return <span className="text-gray-300">—</span>;
    if (diff.tone === 'even') return <span className={toneClass.even}>{t('delivery.routeReplay.diff.even')}</span>;
    const sign = diff.delta! < 0 ? '-' : '+';
    return <span className={toneClass[diff.tone]}>{`${sign}${spec.fmt(Math.abs(diff.delta!))}`}</span>;
}

/** 预估指标网格（里程/时长突出 + 3 个计数）；传 baseline 时在里程/时长下显示相对差值 */
const MetricsEstimate: React.FC<{
    metrics: ReplayMetricsFields | null;
    loading?: boolean;
    baseline?: ReplayMetricsFields | null;
}> = ({ metrics, loading, baseline }) => {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;
    if (loading && !metrics) {
        return (
            <div className="h-[104px] flex items-center justify-center text-xs text-gray-400">
                <Spin size="small" className="mr-2" /> {t('delivery.routeReplay.estimate.computing')}
            </div>
        );
    }
    if (!metrics) {
        return <div className="h-[104px] flex items-center justify-center text-xs text-gray-400">{t('delivery.routeReplay.estimate.failed')}</div>;
    }
    const [dist, dur] = METRIC_SPECS; // 只展示总里程 + 预计时长；其余 3 指标在「策略对比」表
    return (
        <div className="grid grid-cols-2 gap-2">
            {/* 总行驶距离 */}
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-3.5">
                <div className="text-[11px] text-gray-400 flex items-center gap-1">
                    <ArrowLeftOutlined /> {t('delivery.routeReplay.estimate.distanceLabel')}
                </div>
                <div className="mt-2 leading-none">
                    <span className="text-2xl font-bold text-gray-900">
                        {metrics.totalDistanceKm != null ? metrics.totalDistanceKm.toFixed(1) : '—'}
                    </span>
                    {metrics.totalDistanceKm != null && <span className="text-xs text-gray-500 ml-0.5">km</span>}
                </div>
                {baseline && (
                    <div className="text-[11px] mt-1">
                        <DeltaText spec={dist} diff={compare(metrics.totalDistanceKm, baseline.totalDistanceKm)} />
                    </div>
                )}
            </div>
            {/* 预估行驶时间 */}
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-3.5">
                <div className="text-[11px] text-gray-400 flex items-center gap-1">
                    <ClockCircleOutlined /> {t('delivery.routeReplay.estimate.durationLabel')}
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900 leading-none">{fmtDur(metrics.totalDurationMinutes)}</div>
                {baseline && (
                    <div className="text-[11px] mt-1">
                        <DeltaText spec={dur} diff={compare(metrics.totalDurationMinutes, baseline.totalDurationMinutes)} />
                    </div>
                )}
            </div>
        </div>
    );
};

/** 策略对比表：版本 × [距离/时间/左转/掉头/红灯]，算法行 + 已保存版本（点选载入） */
const VersionCompareList: React.FC<{
    algorithm: ReplayMetricsFields | null;
    versions: ReplayManualVersion[];
    onLoad?: (v: ReplayManualVersion) => void;
    onDelete?: (v: ReplayManualVersion) => void;
    selectedId?: number | null;
}> = ({ algorithm, versions, onLoad, onDelete, selectedId }) => {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;

    const headerCells = METRIC_SPECS.map((s) => (
        <div key={s.key} className={`${COLW[s.key]} shrink-0 text-right text-[10px] leading-tight text-gray-400 whitespace-nowrap`}>
            {t(`delivery.routeReplay.metricShort.${s.labelKey}`)}
        </div>
    ));

    const metricCells = (m: ReplayMetricsFields | null) =>
        METRIC_SPECS.map((s) => (
            <div key={s.key} className={`${COLW[s.key]} shrink-0 text-right text-xs leading-tight tabular-nums text-gray-700 whitespace-nowrap`}>
                {cellValue(s, m)}
            </div>
        ));

    return (
        <div className="text-sm">
            {/* 表头 */}
            <div className="flex items-end gap-x-0.5 px-2 pb-2.5 border-b border-gray-100">
                <div className="flex-1 min-w-[90px] text-[11px] text-gray-400">{t('delivery.routeReplay.compare.colVersion')}</div>
                {headerCells}
            </div>

            {/* 算法行（浅蓝高亮） */}
            <div className="flex items-center gap-x-0.5 px-2 py-2.5 my-3 rounded-lg bg-[#eef2ff]">
                <div className="flex-1 min-w-[90px] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: REPLAY_PRIMARY }} />
                    <span className="font-semibold text-gray-800 whitespace-nowrap">{t('delivery.routeReplay.compare.algorithmRow')}</span>
                </div>
                {metricCells(algorithm)}
            </div>

            {/* 已保存版本区 */}
            <div className="px-2 py-2 mt-2 bg-gray-50 text-[11px] text-gray-400 rounded">{t('delivery.routeReplay.compare.savedBand')}</div>

            {versions.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-4">{t('delivery.routeReplay.compare.empty')}</div>
            ) : (
                <>
                    {versions.map((v) => (
                        <div key={v.id} className="relative mt-3">
                            <button
                                className={`w-full flex items-center gap-x-0.5 px-2 py-2.5 text-left rounded-lg disabled:cursor-default ${
                                    selectedId === v.id
                                        ? 'bg-orange-100'
                                        : 'bg-orange-50 hover:bg-orange-100 disabled:hover:bg-orange-50'
                                }`}
                                onClick={() => onLoad?.(v)}
                                disabled={!onLoad}
                            >
                                <div className="flex-1 min-w-[90px]">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                                        <span className="font-semibold text-orange-600 whitespace-nowrap">
                                            {t('delivery.routeReplay.compare.versionRow', { no: v.versionNo })}
                                        </span>
                                    </div>
                                    <div className="pl-4 mt-1.5 space-y-1 leading-tight">
                                        {v.savedByName && <div className="text-[11px] text-gray-400 truncate">{v.savedByName}</div>}
                                        <div className="text-[11px] text-gray-400">{dayjs(v.savedAt).format('M/D HH:mm')}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-x-0.5 mt-2">{metricCells(v)}</div>
                            </button>
                            {onDelete && (
                                <Popconfirm
                                    title={t('delivery.routeReplay.manual.deleteConfirm')}
                                    okText={t('delivery.routeReplay.manual.deleteOk')}
                                    cancelText={t('delivery.routeReplay.manual.deleteCancel')}
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => onDelete(v)}
                                >
                                    <button
                                        className="absolute top-1.5 right-1.5 p-1 rounded text-orange-500 hover:text-orange-600 hover:bg-orange-100"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <DeleteOutlined />
                                    </button>
                                </Popconfirm>
                            )}
                        </div>
                    ))}
                    <div className="text-center text-[11px] text-gray-400 pt-3 px-2">{t('delivery.routeReplay.compare.footer')}</div>
                </>
            )}
        </div>
    );
};

interface ReplaySidebarProps {
    routes: ReplayRouteOption[];
    selectedDspTaskId?: number;
    onSelectRoute: (dspTaskId?: number) => void;
    areaName?: string;
    detail: ReplayRouteDetail | null;
    algorithmMetrics: ReplayMetricsFields | null;
    algorithmLoading?: boolean;
    savedVersions: ReplayManualVersion[];
    manualMode?: boolean;
    manualMetrics?: ReplayMetricsFields | null;
    loadedVersionId?: number | null;
    onLoadVersion?: (v: ReplayManualVersion) => void;
    onDeleteVersion?: (v: ReplayManualVersion) => void;
    onEnterManual: () => void;
}

/** 左侧操作栏：下拉选路 + G4 基础信息 + 指标/对比 */
const ReplaySidebar: React.FC<ReplaySidebarProps> = ({
    routes,
    selectedDspTaskId,
    onSelectRoute,
    areaName,
    detail,
    algorithmMetrics,
    algorithmLoading,
    savedVersions,
    manualMode,
    manualMetrics,
    loadedVersionId,
    onLoadVersion,
    onDeleteVersion,
    onEnterManual,
}) => {
    const { t } = useTranslation();
    const route = detail?.route;

    return (
        <div className="w-fit shrink-0 h-full overflow-y-auto border-r border-gray-200 bg-gray-50 p-3 space-y-2">
            {/* 1. 选择回放线路（本区域下的路线下拉） */}
            <Card size="small" title={<span className="text-xs font-normal text-gray-600">{t('delivery.routeReplay.sidebar.selectRoute')}</span>}>
                <Select
                    className="w-full"
                    placeholder={t('delivery.routeReplay.sidebar.route')}
                    value={selectedDspTaskId}
                    onChange={onSelectRoute}
                    options={routes.map((r) => ({ label: r.routeName || `#${r.dspTaskId}`, value: r.dspTaskId }))}
                    notFoundContent={t('delivery.routeReplay.sidebar.empty')}
                />
            </Card>

            {/* 2. 线路基础信息 */}
            <Card size="small">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: REPLAY_PRIMARY }} />
                    <span className="text-sm font-semibold text-gray-800">{route?.routeName ?? '—'}</span>
                </div>
                <div className="text-[11px] text-gray-400 mt-1">
                    {route ? `${route.warehouseName ?? ''} · ${route.areaName ?? ''} · ${route.taskDate ?? ''}` : t('delivery.routeReplay.sidebar.routeMeta')}
                </div>

                <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 mt-2">
                    <div className="text-[11px] text-gray-400">{t('delivery.routeReplay.sidebar.driver')}</div>
                    <div className="text-sm font-semibold text-gray-800">{route?.driverName ?? t('delivery.routeReplay.sidebar.empty')}</div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                        <div className="text-[11px] text-gray-400">{t('delivery.routeReplay.sidebar.parcelCount')}</div>
                        <div className="text-sm font-semibold text-gray-800">{route?.assignedParcels != null ? t('delivery.routeReplay.sidebar.parcelUnit', { count: route.assignedParcels }) : t('delivery.routeReplay.sidebar.empty')}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                        <div className="text-[11px] text-gray-400">{t('delivery.routeReplay.sidebar.stopRange')}</div>
                        <div className="text-sm font-semibold text-gray-800">{route?.stopRange ? t('delivery.routeReplay.sidebar.stopRangeValue', { range: route.stopRange }) : t('delivery.routeReplay.sidebar.empty')}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                        <div className="text-[11px] text-gray-400">{t('delivery.routeReplay.sidebar.startPoint')}</div>
                        <div className="text-sm">
                            {detail?.startPoint ? <span className="text-green-600 font-semibold">{t('delivery.routeReplay.sidebar.annotated')}</span> : <span className="text-gray-400">{t('delivery.routeReplay.sidebar.empty')}</span>}
                        </div>
                    </div>
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                        <div className="text-[11px] text-gray-400">{t('delivery.routeReplay.sidebar.endPoint')}</div>
                        <div className="text-sm">
                            {detail?.endPoint ? <span className="text-gray-800 font-semibold">{t('delivery.routeReplay.sidebar.annotated')}</span> : <span className="text-gray-400">{t('delivery.routeReplay.sidebar.empty')}</span>}
                        </div>
                    </div>
                </div>
            </Card>

            {/* 3. 当前策略预估数据 + 手动调整按钮 */}
            <Card size="small" title={<span className="text-xs font-normal text-gray-600">{t('delivery.routeReplay.sidebar.estimateTitle')}</span>}>
                {!manualMode && (
                    <div className="inline-block text-xs font-medium text-gray-800 rounded bg-gray-100 px-3 py-1 mb-2">{t('delivery.routeReplay.sidebar.algorithmResult')}</div>
                )}
                <MetricsEstimate
                    metrics={manualMode && manualMetrics ? manualMetrics : algorithmMetrics}
                    loading={algorithmLoading}
                    baseline={manualMode && manualMetrics ? algorithmMetrics : undefined}
                />
                {manualMode ? (
                    <div className="mt-2 text-[11px] text-gray-400">
                        — {t('delivery.routeReplay.estimate.manualBaseline')}：
                        <span className="text-orange-500 font-medium">{t('delivery.routeReplay.sidebar.algorithmResult')}</span>
                    </div>
                ) : (
                    <Button type="primary" block className="mt-3" style={{ height: 44 }} disabled={!detail} onClick={onEnterManual}>
                        {t('delivery.routeReplay.sidebar.enterManual')} <ArrowRightOutlined />
                    </Button>
                )}
            </Card>

            {/* 4. 策略对比（算法 + 已保存手动版本 G7） */}
            <Card size="small" title={<span className="text-xs font-normal text-gray-600">{t('delivery.routeReplay.sidebar.compareTitle')}</span>}>
                <VersionCompareList algorithm={algorithmMetrics} versions={savedVersions} onLoad={onLoadVersion} onDelete={onDeleteVersion} selectedId={loadedVersionId} />
            </Card>
        </div>
    );
};

export default ReplaySidebar;
